import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, FileText, Trash2, Loader2, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../../lib/api";
import { RerunModal } from "./ReadinessReportCard";
import { UploadFailedBadge } from "./UploadFailedBadge";

const DOC_TYPES = [
  { key: "pitch_deck", label: "Pitch deck" },
  { key: "financial_model", label: "Financial model" },
  { key: "mgmt_accounts", label: "Management accounts" },
  { key: "legal", label: "Legal docs" },
  { key: "bank_statements", label: "Bank statements" },
  { key: "audited_financials", label: "Audited financials" },
  { key: "other", label: "Other supporting document" },
];
const label = (k) => DOC_TYPES.find((d) => d.key === k)?.label || k;
// One "current version" makes sense for these; statements/accounts are naturally multi-file.
const SINGLE_VERSION_TYPES = new Set(["pitch_deck", "financial_model", "legal", "audited_financials"]);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

/** "Replace your previous pitch deck?" — shown after uploading a second version of a single-version doc type. */
export function ReplaceOldVersionModal({ fresh, olds, busy, onKeep, onReplace }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="replace-old-version-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A8B85]">Keep your pack clean</p>
        <h3 className="mt-1 text-base font-extrabold text-neutral-900">Replace your previous {label(fresh.doc_type).toLowerCase()}?</h3>
        <p className="mt-2 text-sm text-neutral-600">
          <b>{fresh.filename}</b> was uploaded. You already had {olds.length === 1 ? "an older version" : `${olds.length} older versions`}:
        </p>
        <ul className="mt-2 space-y-1 rounded-lg bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
          {olds.map((d) => (
            <li key={d.doc_id} className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-neutral-400" /> <span className="truncate">{d.filename}</span><span className="ml-auto shrink-0 text-neutral-400">{fmt(d.uploaded_at)}</span></li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-neutral-500">Funders (and our analysis) work best with one current version per document type.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onKeep} disabled={busy} data-testid="replace-old-keep-both"
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">Keep both</button>
          <button type="button" onClick={onReplace} disabled={busy} data-testid="replace-old-confirm"
            className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#087a74] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Replace old version
          </button>
        </div>
      </div>
    </div>
  );
}
export const SINGLE_VERSION_DOC_TYPES = SINGLE_VERSION_TYPES;

/** Presign → S3 PUT → confirm, with legacy multipart fallback (mirrors Onboarding.postUpload). */
async function uploadOne(file, doc_type, onPct) {
  let presign;
  try {
    presign = (await api.post("/documents/presign", {
      filename: file.name, content_type: file.type || "application/octet-stream", doc_type, size: file.size,
    })).data;
  } catch (e) {
    if (e?.response?.status !== 503) throw e;
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", doc_type);
    const legacy = await api.post("/documents", fd, { timeout: 120000,
      onUploadProgress: (evt) => evt.total && onPct(Math.min(99, Math.round((evt.loaded / evt.total) * 100))) });
    return legacy.data;
  }
  const axios = (await import("axios")).default;
  await axios.put(presign.put_url, file, {
    headers: { "Content-Type": file.type || "application/octet-stream" }, timeout: 0, withCredentials: false,
    transformRequest: [(d) => d],
    onUploadProgress: (evt) => evt.total && onPct(Math.min(99, Math.round((evt.loaded / evt.total) * 100))),
  });
  return (await api.post("/documents/confirm", { doc_id: presign.doc_id })).data;
}

/**
 * Banner on the Capital Readiness Report page: upload updated documents and
 * re-run the analysis without going back through onboarding.
 */
export default function UpdateDocsRerunBanner({ report, locked = false, onRerunDone, onBuy }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [docType, setDocType] = useState("pitch_deck");
  const [uploading, setUploading] = useState(null); // { name, pct }
  const [uploadedSince, setUploadedSince] = useState(0);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [replacePrompt, setReplacePrompt] = useState(null); // { fresh, olds: [] }
  const [replacing, setReplacing] = useState(false);
  const fileRef = useRef(null);
  const retryRef = useRef(null); // failed doc being replaced via "Upload again"

  const loadDocs = () => api.get("/documents/me").then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  useEffect(() => { if (open && docs === null) loadDocs(); }, [open, docs]);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const retry = retryRef.current;
    retryRef.current = null;
    const type = retry?.doc_type || docType;
    for (const file of list) {
      setUploading({ name: file.name, pct: 0 });
      const tid = toast.loading(`Uploading ${file.name}…`, { duration: Infinity });
      try {
        const olds = SINGLE_VERSION_TYPES.has(type)
          ? (docs || []).filter((d) => d.doc_type === type && d.upload_status !== "failed") : [];
        const saved = await uploadOne(file, type, (pct) => setUploading({ name: file.name, pct }));
        if (retry?.doc_id) {
          try { await api.delete(`/documents/${retry.doc_id}`); } catch (_) { /* already gone */ }
          setDocs((d) => (d || []).filter((x) => x.doc_id !== retry.doc_id));
        }
        setDocs((d) => [...(d || []), saved]);
        setUploadedSince((n) => n + 1);
        if (olds.length && saved?.doc_id) setReplacePrompt({ fresh: saved, olds });
        toast.success(`${file.name} uploaded`, { id: tid,
          description: saved?.vision_pending ? "Reading it in the background — re-run once it's done." : `Saved as ${label(type)}`, duration: 4000 });
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Couldn't upload ${file.name}`, { id: tid, duration: 6000 });
      }
    }
    setUploading(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.filename}?`)) return;
    try {
      await api.delete(`/documents/${d.doc_id}`);
      setDocs((ds) => (ds || []).filter((x) => x.doc_id !== d.doc_id));
      toast.success("Document removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
  };

  const replaceOld = async () => {
    if (!replacePrompt) return;
    setReplacing(true);
    const ids = replacePrompt.olds.map((d) => d.doc_id);
    try {
      await Promise.all(ids.map((id) => api.delete(`/documents/${id}`)));
      setDocs((ds) => (ds || []).filter((d) => !ids.includes(d.doc_id)));
      toast.success(`Replaced your previous ${label(replacePrompt.fresh.doc_type).toLowerCase()}`, {
        description: `${replacePrompt.fresh.filename} is now the only version in your pack.`,
      });
      setReplacePrompt(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove the old version"); }
    finally { setReplacing(false); }
  };

  const credits = report?.credits_balance ?? 0;
  const rerunClick = () => {
    if (locked) return;
    if (credits <= 0) { onBuy?.(); return; }
    setRerunOpen(true);
  };

  return (
    <section data-testid="update-docs-rerun-banner"
      className="mt-5 overflow-hidden rounded-2xl border border-[#0A8B85]/30 bg-gradient-to-r from-[#E8F6F5] to-white">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0A8B85]/15">
            <RefreshCw className="h-4 w-4 text-[#0A8B85]" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-neutral-900">Updated your documents? Upload the new versions and re-run your analysis.</p>
            <p className="mt-0.5 text-xs text-neutral-600">
              Re-running with fresh documents is how your score moves. {credits > 0
                ? <>You have <b>{credits} re-run credit{credits === 1 ? "" : "s"}</b>.</>
                : <>You&rsquo;re out of re-run credits — buy a new pack to continue.</>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="banner-upload-toggle"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#0A8B85] bg-white px-4 py-2 text-xs font-bold text-[#0A8B85] hover:bg-[#0A8B85]/5">
            <Upload className="h-3.5 w-3.5" /> Upload new documents {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          <button type="button" onClick={rerunClick} disabled={locked} data-testid="banner-rerun-btn"
            title={locked ? "Your score is locked while a Verified review is in progress" : ""}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#004B62] px-4 py-2 text-xs font-bold text-white hover:bg-[#00303F] disabled:cursor-not-allowed disabled:opacity-50">
            <RefreshCw className="h-3.5 w-3.5" /> {credits > 0 ? "Re-run my analysis (1 credit)" : "Buy re-run credits"}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[#0A8B85]/20 bg-white px-5 py-4" data-testid="banner-upload-panel">
          <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">What are you uploading?</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="banner-doc-type"
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-800 focus:border-[#0A8B85] focus:outline-none">
                {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                data-testid="banner-dropzone"
                className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#0A8B85]/40 bg-[#F4FBFA] px-4 py-6 text-center hover:border-[#0A8B85]">
                {uploading ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" />
                    <p className="mt-2 text-sm font-semibold text-neutral-800">{uploading.name}</p>
                    <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-[#0A8B85]/15">
                      <div className="h-full bg-[#0A8B85] transition-[width]" style={{ width: `${uploading.pct}%` }} />
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-[#0A8B85]" />
                    <p className="mt-2 text-sm font-semibold text-neutral-800">Drop files here or click to browse</p>
                    <p className="mt-0.5 text-[11px] text-neutral-500">PDF, PPT, XLS, CSV · up to 25 MB each · multiple files OK</p>
                  </>
                )}
                <input ref={fileRef} type="file" multiple className="hidden" data-testid="banner-file-input"
                  accept=".pdf,.ppt,.pptx,.xls,.xlsx,.csv,.doc,.docx,image/*"
                  onChange={(e) => onFiles(e.target.files)} />
              </div>
              {uploadedSince > 0 && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="banner-uploaded-note">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {uploadedSince} new file{uploadedSince === 1 ? "" : "s"} added — click &ldquo;Re-run my analysis&rdquo; when you&rsquo;re done.
                </p>
              )}
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Your current documents</p>
              <ul className="mt-1 max-h-56 divide-y divide-neutral-100 overflow-y-auto rounded-lg border border-neutral-200" data-testid="banner-doc-list">
                {docs === null && <li className="px-3 py-3 text-xs text-neutral-400">Loading…</li>}
                {docs && docs.length === 0 && <li className="px-3 py-3 text-xs text-neutral-400">No documents yet.</li>}
                {(docs || []).map((d) => (
                  <li key={d.doc_id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`banner-doc-${d.doc_id}`}>
                    <FileText className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                    <span className="min-w-0 flex-1 truncate font-semibold text-neutral-800" title={d.filename}>{d.filename}</span>
                    <UploadFailedBadge doc={d}
                      onRetry={() => { retryRef.current = d; setDocType(d.doc_type); fileRef.current?.click(); }} />
                    <span className="hidden shrink-0 text-neutral-500 sm:inline">{label(d.doc_type)} · {fmt(d.uploaded_at)}</span>
                    <button type="button" onClick={() => remove(d)} aria-label="Remove" className="shrink-0 rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {replacePrompt && (
        <ReplaceOldVersionModal fresh={replacePrompt.fresh} olds={replacePrompt.olds} busy={replacing}
          onKeep={() => setReplacePrompt(null)} onReplace={replaceOld} />
      )}

      {rerunOpen && (
        <RerunModal checklist={report?.checklist || []} balance={credits}
          onClose={() => setRerunOpen(false)}
          onSuccess={(r) => { setRerunOpen(false); onRerunDone?.(r); }} />
      )}
    </section>
  );
}
